import React, { useState, useEffect, useMemo } from 'react';
import { Table, Spinner, Tabs, Tab, Modal, Button, Badge, Form, Pagination, Card, Row, Col, InputGroup } from 'react-bootstrap';
import { FaSearch, FaTrashAlt, FaUserCircle, FaMapMarkerAlt, FaEnvelope, FaPhoneAlt, FaCalendarAlt, FaHistory, FaFilter, FaFileExport, FaUserPlus, FaLock, FaUndo } from 'react-icons/fa';
import api from '../../services/api';
import { toast } from 'react-toastify';
import './styles.css';

const RegisteredUsers = () => {
  const [registeredUsers, setRegisteredUsers] = useState([]);
  const [loggedInUsers, setLoggedInUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [key, setKey] = useState('registered');
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [adding, setAdding] = useState(false);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 5;

  // New User Form State
  const [newUser, setNewUser] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    location: ''
  });

  const fetchRegisteredUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('https://api.collegeall.in/api/v1/user_list');
      setRegisteredUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching registered users:', error);
      toast.error('Failed to load user directory.');
    } finally {
      setLoading(false);
    }
  };

  const fetchLoggedInUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('https://api.collegeall.in/api/v1/loggedin-users');
      setLoggedInUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching logged-in users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegisteredUsers();
  }, []);

  useEffect(() => {
    if (key === 'loggedin') {
      fetchLoggedInUsers();
    }
  }, [key]);

  const locationsList = useMemo(() => {
    const list = key === 'registered' ? registeredUsers : loggedInUsers;
    const unique = [...new Set(list.map(u => u.location).filter(Boolean))];
    return unique.sort();
  }, [registeredUsers, loggedInUsers, key]);

  const filteredUsers = () => {
    const list = key === 'registered' ? registeredUsers : loggedInUsers;
    return list.filter(user => {
      const matchesSearch = user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            user.email?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesLocation = !filterLocation || user.location === filterLocation;
      return matchesSearch && matchesLocation;
    });
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilterLocation('');
  };

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers().slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers().length / usersPerPage);

  const fetchUserProfile = async (email) => {
    setProfileLoading(true);
    try {
      const response = await api.get(`https://api.collegeall.in/api/v1/get-profile?email=${email}`);
      setSelectedUserProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleRowClick = (user) => {
    setShowProfileModal(true);
    fetchUserProfile(user.email);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    setDeleting(true);
    try {
      await api.delete(`https://api.collegeall.in/api/v1/delete-user/${userToDelete.id}`);
      setRegisteredUsers(prev => prev.filter(u => u.id !== userToDelete.id));
      setLoggedInUsers(prev => prev.filter(u => u.user_id !== userToDelete.id));
      setShowDeleteModal(false);
      setUserToDelete(null);
      toast.success('User account archived successfully.');
    } catch (error) {
      toast.error('Operation failed. Could not delete user.');
    } finally {
      setDeleting(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post('https://api.collegeall.in/api/v1/admin/register-user', newUser);
      setShowAddModal(false);
      setNewUser({ full_name: '', email: '', phone: '', password: '', location: '' });
      fetchRegisteredUsers();
      toast.success('New talent account created successfully.');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add new user.');
    } finally {
      setAdding(false);
    }
  };

  const renderAvatar = (user, size = 40) => {
    if (!user) return null;
    const name = user.full_name || 'User';
    return user.profile_pic ? (
      <img
        src={`https://api.collegeall.in/api/v1/uploads/${user.profile_pic}`}
        alt="Avatar"
        className="user-avatar-img shadow-sm"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = ''; 
          e.target.style.display = 'none';
          const placeholder = e.target.nextSibling;
          if (placeholder) placeholder.style.display = 'flex';
        }}
        style={{ width: size, height: size, borderRadius: '10px', objectFit: 'cover' }}
      />
    ) : (
      <div 
        className="user-avatar-placeholder shadow-sm" 
        style={{ width: size, height: size, fontSize: size * 0.4, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#6366f1', color: 'white', fontWeight: 'bold' }}
      >
        {name.charAt(0).toUpperCase()}
      </div>
    );
  };

  const renderPaginationItems = () => {
    const items = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        items.push(
          <Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>
            {i}
          </Pagination.Item>
        );
      }
      return items;
    }

    let start = currentPage - 1;
    let end = currentPage + 1;

    if (start <= 1) {
      start = 1;
      end = 3;
    } else if (end >= totalPages) {
      end = totalPages;
      start = totalPages - 2;
    }

    if (start > 1) {
      items.push(<Pagination.Ellipsis key="start-el" disabled />);
    }

    for (let i = start; i <= end; i++) {
      if (i < totalPages) {
        items.push(
          <Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>
            {i}
          </Pagination.Item>
        );
      }
    }

    if (end < totalPages - 1) {
      items.push(<Pagination.Ellipsis key="end-el" disabled />);
    }

    items.push(
      <Pagination.Item key={totalPages} active={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>
        {totalPages}
      </Pagination.Item>
    );

    return items;
  };

  return (
    <div className="user-mgmt-container p-4">
      {/* Header & Stats */}
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">User Directory</h2>
          <p className="text-muted small mb-0">Manage and monitor all platform participants</p>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" className="btn-modern shadow-sm">
            <FaFileExport className="me-2" /> Export
          </Button>
          <Button variant="primary" className="btn-modern shadow-sm" onClick={() => setShowAddModal(true)}>
            <FaUserPlus className="me-2" /> Add New User
          </Button>
        </div>
      </div>

      <Row className="g-3 mb-4">
        <Col md={4}>
          <Card className="border-0 shadow-sm metric-card p-3">
            <div className="d-flex align-items-center">
              <div className="metric-icon bg-indigo-subtle text-indigo">
                <FaUserCircle />
              </div>
              <div className="ms-3">
                <div className="small text-muted fw-bold text-uppercase">Total Registered</div>
                <h4 className="fw-bold mb-0">{registeredUsers.length}</h4>
              </div>
            </div>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm metric-card p-3">
            <div className="d-flex align-items-center">
              <div className="metric-icon bg-success-subtle text-success">
                <FaHistory />
              </div>
              <div className="ms-3">
                <div className="small text-muted fw-bold text-uppercase">Currently Active</div>
                <h4 className="fw-bold mb-0">{loggedInUsers.filter(u => !u.logout_time).length}</h4>
              </div>
            </div>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm metric-card p-3">
            <div className="d-flex align-items-center">
              <div className="metric-icon bg-warning-subtle text-warning">
                <FaCalendarAlt />
              </div>
              <div className="ms-3">
                <div className="small text-muted fw-bold text-uppercase">New This Month</div>
                <h4 className="fw-bold mb-0">12</h4>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
        <div className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center">
          <Tabs
            activeKey={key}
            onSelect={(k) => { setKey(k); setCurrentPage(1); }}
            className="modern-tabs border-0"
          >
            <Tab eventKey="registered" title="Registered List" />
            <Tab eventKey="loggedin" title="Live Sessions" />
          </Tabs>

          <div className="d-flex gap-2 align-items-center">
            <InputGroup className="search-group">
              <InputGroup.Text className="bg-light border-0">
                <FaSearch className="text-muted small" />
              </InputGroup.Text>
              <Form.Control
                className="bg-light border-0 small shadow-none"
                placeholder="Search name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            <Button 
              variant={showFilters ? "primary" : "light"} 
              className={`border shadow-none btn-icon ${showFilters ? 'text-white' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaFilter />
            </Button>
          </div>
        </div>

        {/* Filter Bar */}
        {showFilters && (
          <div className="px-3 py-2 bg-light-subtle border-bottom animate-fade-in">
            <Row className="g-2 align-items-center">
              <Col md={4}>
                <Form.Select 
                  size="sm" 
                  className="border-0 shadow-sm x-small"
                  value={filterLocation}
                  onChange={(e) => setFilterLocation(e.target.value)}
                >
                  <option value="">All Locations</option>
                  {locationsList.map((loc, i) => (
                    <option key={i} value={loc}>{loc}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={4}>
                <Button variant="link" className="text-muted x-small p-0 text-decoration-none d-flex align-items-center gap-1" onClick={resetFilters}>
                  <FaUndo size={8} /> Reset Filters
                </Button>
              </Col>
            </Row>
          </div>
        )}

        <div className="table-responsive">
          <Table hover className="modern-table mb-0 align-middle">
            <thead>
              <tr>
                <th className="ps-4">User</th>
                <th>Contact</th>
                <th>Location</th>
                {key === 'registered' ? <th>Joined</th> : <th>Last Login</th>}
                <th className="text-end pe-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center py-5">
                    <Spinner animation="border" size="sm" variant="primary" />
                  </td>
                </tr>
              ) : currentUsers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-5 text-muted small italic">
                    No matching users found
                  </td>
                </tr>
              ) : (
                currentUsers.map((user) => (
                  <tr key={user.id || user.user_id} className="cursor-pointer clickable-row" onClick={() => handleRowClick(user)}>
                    <td className="ps-4">
                      <div className="d-flex align-items-center">
                        {renderAvatar(user)}
                        <div className="ms-3">
                          <div className="fw-bold text-dark mb-0">{user.full_name}</div>
                          <div className="small text-muted">ID: {user.id || user.user_id}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="small text-dark fw-medium d-flex align-items-center mb-1">
                        <FaEnvelope className="me-2 text-muted x-small" /> {user.email}
                      </div>
                      {user.phone && (
                        <div className="small text-muted d-flex align-items-center">
                          <FaPhoneAlt className="me-2 text-muted x-small" /> {user.phone}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="d-flex align-items-center small text-muted">
                        <FaMapMarkerAlt className="me-1 text-danger x-small" />
                        {user.location || 'Not Set'}
                      </div>
                    </td>
                    <td>
                      <div className="small text-dark fw-medium">
                        {new Date(user.cdate || user.login_time).toLocaleDateString('en-IN', {
                          day: '2-digit', month: 'short', year: 'numeric'
                        })}
                      </div>
                      {key === 'loggedin' && (
                        <Badge bg={user.logout_time ? "light" : "success"} text={user.logout_time ? "dark" : "white"} className="x-small border">
                          {user.logout_time ? 'Terminated' : 'Active Now'}
                        </Badge>
                      )}
                    </td>
                    <td className="text-end pe-4">
                      <Button 
                        variant="light" 
                        size="sm" 
                        className="btn-icon text-danger shadow-none"
                        onClick={(e) => {
                          e.stopPropagation();
                          setUserToDelete(user);
                          setShowDeleteModal(true);
                        }}
                      >
                        <FaTrashAlt />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        {totalPages > 1 && (
          <div className="p-3 bg-light border-top d-flex justify-content-between align-items-center">
            <div className="small text-muted">
              Showing {indexOfFirstUser + 1} to {Math.min(indexOfLastUser, filteredUsers().length)} of {filteredUsers().length} entries
            </div>
            <Pagination className="mb-0 modern-pagination">
              <Pagination.Prev onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} />
              {renderPaginationItems()}
              <Pagination.Next onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} />
            </Pagination>
          </div>
        )}
      </Card>

      {/* Modern Profile Drawer/Modal */}
      <Modal show={showProfileModal} onHide={() => setShowProfileModal(false)} size="lg" centered className="profile-modal">
        <Modal.Header closeButton className="border-0 pb-0" />
        <Modal.Body className="pt-0">
          {profileLoading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : selectedUserProfile ? (
            <div className="profile-wrapper">
              <div className="profile-header-strip mb-4 d-flex align-items-center">
                {/* Fixed profile pic access using renderAvatar helper */}
                {renderAvatar(selectedUserProfile.contact || selectedUserProfile, 100)}
                <div className="ms-4">
                  <h2 className="fw-bold mb-1">{(selectedUserProfile.contact || selectedUserProfile).full_name}</h2>
                  <div className="d-flex flex-wrap gap-3 text-muted small">
                    <span className="d-flex align-items-center"><FaEnvelope className="me-1 text-primary" /> {(selectedUserProfile.contact || selectedUserProfile).email}</span>
                    {(selectedUserProfile.contact || selectedUserProfile).phone && (
                      <span className="d-flex align-items-center"><FaPhoneAlt className="me-1 text-success" /> {(selectedUserProfile.contact || selectedUserProfile).phone}</span>
                    )}
                    {(selectedUserProfile.contact || selectedUserProfile).location && (
                      <span className="d-flex align-items-center"><FaMapMarkerAlt className="me-1 text-danger" /> {(selectedUserProfile.contact || selectedUserProfile).location}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="profile-content-area">
                <Row>
                  <Col md={12}>
                    <div className="section-label mb-3">Professional Overview</div>
                    <div className="bg-light p-3 rounded-3 mb-4 small line-height-relaxed">
                      {(selectedUserProfile.contact || selectedUserProfile).summary || 'No bio provided for this candidate.'}
                    </div>

                    <div className="section-label mb-3">Skills & Proficiencies</div>
                    <div className="d-flex flex-wrap gap-2 mb-4">
                      {selectedUserProfile.skills?.length > 0 ? (
                        selectedUserProfile.skills.map(skill => (
                          <Badge key={skill.id} bg="primary-subtle" text="primary" className="px-3 py-2 border-0 fw-medium">
                            {skill.skill_name}
                          </Badge>
                        ))
                      ) : <span className="text-muted small italic">Not listed</span>}
                    </div>

                    <div className="section-label mb-3">Recent Experience</div>
                    <div className="timeline-minimal ps-3">
                      {selectedUserProfile.experience?.length > 0 ? (
                        selectedUserProfile.experience.map(exp => (
                          <div key={exp.id} className="timeline-node mb-4">
                            <div className="fw-bold text-dark small">{exp.job_title}</div>
                            <div className="text-primary x-small fw-bold">{exp.company}</div>
                            <div className="text-muted x-small mb-2">{exp.start_date} - {exp.end_date || 'Present'}</div>
                            <div className="x-small text-muted">{exp.description}</div>
                          </div>
                        ))
                      ) : <span className="text-muted small italic">No experience added</span>}
                    </div>
                    
                    <div className="section-label mb-3">Education</div>
                    <div className="timeline-minimal ps-3">
                      {selectedUserProfile.education?.length > 0 ? (
                        selectedUserProfile.education.map(edu => (
                          <div key={edu.id} className="timeline-node mb-4">
                            <div className="fw-bold text-dark small">{edu.degree}</div>
                            <div className="text-primary x-small fw-bold">{edu.university}</div>
                            <div className="text-muted x-small mb-2">{edu.start_date} - {edu.end_date || 'Present'}</div>
                            <div className="x-small text-muted">{edu.description}</div>
                          </div>
                        ))
                      ) : <span className="text-muted small italic">No education history added</span>}
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          ) : <div className="text-center py-4">No data</div>}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="light" onClick={() => setShowProfileModal(false)} className="fw-bold small">CLOSE PROFILE</Button>
        </Modal.Footer>
      </Modal>

      {/* Add New User Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered className="add-user-modal">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">Create New Account</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddUser}>
          <Modal.Body className="p-4">
            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-muted">FULL NAME</Form.Label>
                  <InputGroup>
                    <InputGroup.Text className="bg-light border-0"><FaUserCircle className="text-muted" /></InputGroup.Text>
                    <Form.Control 
                      className="bg-light border-0 shadow-none" 
                      required 
                      placeholder="e.g. John Doe"
                      value={newUser.full_name}
                      onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-muted">EMAIL ADDRESS</Form.Label>
                  <InputGroup>
                    <InputGroup.Text className="bg-light border-0"><FaEnvelope className="text-muted" /></InputGroup.Text>
                    <Form.Control 
                      type="email" 
                      className="bg-light border-0 shadow-none" 
                      required 
                      placeholder="john@example.com"
                      value={newUser.email}
                      onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-muted">PHONE NUMBER</Form.Label>
                  <InputGroup>
                    <InputGroup.Text className="bg-light border-0"><FaPhoneAlt className="text-muted" /></InputGroup.Text>
                    <Form.Control 
                      type="tel" 
                      className="bg-light border-0 shadow-none" 
                      required 
                      placeholder="+91 XXXXX XXXXX"
                      value={newUser.phone}
                      onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-muted">PASSWORD</Form.Label>
                  <InputGroup>
                    <InputGroup.Text className="bg-light border-0"><FaLock className="text-muted" /></InputGroup.Text>
                    <Form.Control 
                      type="password" 
                      className="bg-light border-0 shadow-none" 
                      required 
                      placeholder="Min 8 characters"
                      value={newUser.password}
                      onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-muted">LOCATION (OPTIONAL)</Form.Label>
                  <InputGroup>
                    <InputGroup.Text className="bg-light border-0"><FaMapMarkerAlt className="text-muted" /></InputGroup.Text>
                    <Form.Control 
                      className="bg-light border-0 shadow-none" 
                      placeholder="e.g. Bangalore, India"
                      value={newUser.location}
                      onChange={(e) => setNewUser({...newUser, location: e.target.value})}
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button variant="light" onClick={() => setShowAddModal(false)} className="fw-bold">CANCEL</Button>
            <Button variant="primary" type="submit" disabled={adding} className="fw-bold px-4">
              {adding ? <Spinner animation="border" size="sm" /> : 'CREATE ACCOUNT'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="confirm-delete-modal">
        <Modal.Body className="text-center p-4">
          <div className="mb-3 text-danger"><FaTrashAlt size={40} /></div>
          <h5 className="fw-bold">Delete Account?</h5>
          <p className="text-muted small">This will permanently remove <strong>{userToDelete?.full_name}</strong> and all associated data from the system.</p>
          <div className="d-grid gap-2">
            <Button variant="danger" disabled={deleting} onClick={handleDeleteUser}>
              {deleting ? <Spinner animation="border" size="sm" /> : 'YES, DELETE PERMANENTLY'}
            </Button>
            <Button variant="light" onClick={() => setShowDeleteModal(false)}>CANCEL</Button>
          </div>
        </Modal.Body>
      </Modal>

      <style>{`
        .user-mgmt-container {
          background: #f8fafc;
          min-height: 100vh;
          font-family: 'Inter', sans-serif;
        }

        .metric-card {
          border-radius: 0.75rem;
          transition: transform 0.2s ease;
        }
        
        .metric-card:hover {
          transform: translateY(-2px);
        }

        .metric-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
        }

        .bg-indigo-subtle { background: #e0e7ff; }
        .text-indigo { color: #4f46e5; }

        .btn-modern {
          border-radius: 8px;
          padding: 0.6rem 1.25rem;
          font-weight: 600;
          font-size: 0.85rem;
        }

        .modern-tabs .nav-link {
          color: #64748b;
          border: 0;
          font-size: 0.85rem;
          font-weight: 600;
          padding: 1rem 1.5rem;
          position: relative;
        }

        .modern-tabs .nav-link.active {
          color: #6366f1;
          background: transparent;
        }

        .modern-tabs .nav-link.active::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: #6366f1;
        }

        .search-group {
          max-width: 280px;
        }

        .search-group .form-control {
          font-size: 0.85rem;
        }

        .btn-icon {
          width: 38px;
          height: 38px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
        }

        .modern-table thead th {
          background: #f8fafc;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #94a3b8;
          border-top: 0;
          font-weight: 700;
        }

        .clickable-row:hover {
          background-color: #f1f5f9;
          cursor: pointer;
        }

        .user-avatar-img, .user-avatar-placeholder {
          border-radius: 10px;
          object-fit: cover;
        }

        .user-avatar-placeholder {
          background: #6366f1;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
        }

        .modern-pagination .page-link {
          border: 0;
          background: transparent;
          color: #64748b;
          font-size: 0.85rem;
          font-weight: 600;
          margin: 0 2px;
          border-radius: 6px;
        }

        .modern-pagination .page-item.active .page-link {
          background: #6366f1;
          color: white;
        }

        .section-label {
          font-size: 0.7rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #94a3b8;
        }

        .timeline-minimal {
          border-left: 2px solid #e2e8f0;
          position: relative;
        }

        .timeline-node {
          position: relative;
        }

        .timeline-node::before {
          content: '';
          position: absolute;
          left: -21px;
          top: 4px;
          width: 10px;
          height: 10px;
          background: #fff;
          border: 2px solid #6366f1;
          border-radius: 50%;
        }

        .x-small { font-size: 0.75rem; }
        .line-height-relaxed { line-height: 1.6; }

        .profile-modal .modal-content, .add-user-modal .modal-content {
          border-radius: 1.25rem;
          border: 0;
          overflow: hidden;
        }

        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default RegisteredUsers;